
import { useEffect, useState } from 'react';

export default function SplashScreen({ onComplete }: { onComplete: () => void }) {
  const [phase, setPhase] = useState<'in' | 'hold' | 'out' | 'done'>('in');

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('hold'), 800);
    const t2 = setTimeout(() => setPhase('out'), 2200);
    const t3 = setTimeout(() => {
      setPhase('done');
      try {
        onComplete?.();
      } catch (e) {
        console.error('SplashScreen: onComplete callback failed', e);
      }
    }, 3200);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [onComplete]);

  if (phase === 'done') return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0F0F0F]"
      style={{
        opacity: phase === 'out' ? 0 : 1,
        transition: phase === 'out' ? 'opacity 1s ease-in-out' : 'none',
        pointerEvents: phase === 'out' ? 'none' : 'all',
      }}
    >
      {/* Main text content */}
      <div
        style={{
          filter:
            phase === 'in' ? 'blur(20px)' : phase === 'hold' ? 'blur(0px)' : 'blur(20px)',
          opacity: phase === 'in' ? 0 : phase === 'hold' ? 1 : 0,
          transform: phase === 'in' ? 'scale(1.05)' : 'scale(1)',
          transition:
            phase === 'hold'
              ? 'filter 0.8s ease-out, opacity 0.8s ease-out, transform 0.8s ease-out'
              : phase === 'out'
              ? 'filter 0.7s ease-in, opacity 0.7s ease-in'
              : 'none',
        }}
        className="text-center px-8"
      >
        <p className="text-white/50 text-sm tracking-[0.35em] uppercase mb-3 font-light">
          Welcome
        </p>
        <h1 className="text-white text-4xl md:text-5xl font-light tracking-widest">
          Simply Elegant
        </h1>
        <div className="flex items-center justify-center gap-3 mt-3">
          <span className="block w-10 h-px bg-orange-500/60"></span>
          <p className="text-orange-400 text-sm tracking-[0.3em] uppercase font-medium">
            at SOUTHDEV
          </p>
          <span className="block w-10 h-px bg-orange-500/60"></span>
        </div>

        {/* Dot loader */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="block w-1.5 h-1.5 rounded-full bg-orange-400/70"
              style={{
                animation: 'splashDotPulse 1.2s ease-in-out infinite',
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Bottom loading bar */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-white/5">
        <div
          className="h-full bg-orange-500/60"
          style={{
            width: phase === 'in' ? '0%' : phase === 'hold' ? '75%' : '100%',
            transition:
              phase === 'hold'
                ? 'width 1.4s cubic-bezier(0.4, 0, 0.2, 1)'
                : phase === 'out'
                ? 'width 0.9s cubic-bezier(0.4, 0, 0.2, 1)'
                : 'none',
          }}
        />
      </div>

      <style>{`
        @keyframes splashDotPulse {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}
